// Reparte las promos de venta de un equipo en los grupos del detalle: regalo, descuento, beneficio y cupón.
// Una combo sale una sola vez, en el grupo de su palanca principal, con las demás palancas abajo.
import { formatMXN } from '@/shared/lib/format/money';
import type {
    AfterSaleBenefit,
    EvaluatedSalePromotion,
    GiftOption,
    StoreSalePromotionItem,
} from '../api/wizardPromotionsContract';
import { conditionsText, isSureKeepOnly, paymentOptionsChipLabel } from './promotionAppliesTo';
import { CATEGORY_PLURAL, isCategoryWide, joinSpanish, pesos } from './saleBenefits';

export type BenefitGroup = 'gift' | 'discount' | 'benefit' | 'coupon';

/** Orden de prioridad de la palanca principal de una combo. */
const GROUP_PRIORITY: readonly BenefitGroup[] = ['discount', 'gift', 'benefit', 'coupon'];

export interface GiftDetail {
    options: GiftOption[];
    /** Cuántas opciones se lleva el cliente. */
    chooseCount: number;
    /** El regalo viene dentro de una promo de venta (combo), no de una promo de regalo. */
    includedInSale: boolean;
}

export interface PromotionPiece {
    promotionId: string;
    name: string;
    group: BenefitGroup;
    /** Lo que da la palanca principal (y las de su mismo grupo). */
    texts: string[];
    /** Solo si la palanca principal es el regalo. */
    gift: GiftDetail | null;
    /** Las otras palancas de la combo: «+ cupón de $200.00 para tu próxima compra». */
    extras: string | null;
    /** Da bono a la cuenta; el detalle le agrega el alcance. */
    walletBonus: boolean;
    /** Solo aplica con crédito SureKeep. */
    sureKeepOnly: boolean;
    /** Chip de forma de pago; `null` sin restricción. */
    paymentLabel: string | null;
    /** Condiciones del cliente en una línea; `null` si no pide nada. */
    conditions: string | null;
}

export type BenefitPieces = Record<BenefitGroup, PromotionPiece[]>;

const BENEFIT_LEVERS = new Set(['WALLET_BONUS', 'AIRTIME', 'EXTENDED_WARRANTY']);

/** «Bono $200 a tu cuenta Fiado», o sin monto si la promo no lo dice. */
export function walletBonusText(amountCents: number | null): string {
    return amountCents === null
        ? 'Bono a tu cuenta Fiado'
        : `Bono ${pesos(amountCents)} a tu cuenta Fiado`;
}

function describeAll(
    benefits: readonly AfterSaleBenefit[],
    describe: (benefit: AfterSaleBenefit) => string,
): string[] {
    return benefits.map(describe).filter((text) => text !== '');
}

function giftPhrase(promo: EvaluatedSalePromotion): string {
    const names = promo.giftOptions.map((o) => o.name).sort((a, b) => a.localeCompare(b, 'es-MX'));
    const chooses = (promo.giftChooseCount ?? 1) < names.length;
    return names.length === 0 ? 'regalo' : `regalo: ${joinSpanish(names, chooses ? 'o' : 'y')}`;
}

function lowerFirst(text: string): string {
    return text.charAt(0).toLocaleLowerCase('es-MX') + text.slice(1);
}

/** Textos de cada grupo que toca la promo; los grupos sin texto quedan fuera. */
function groupTexts(
    promo: EvaluatedSalePromotion,
    describe: (benefit: AfterSaleBenefit) => string,
): Map<BenefitGroup, string[]> {
    const out = new Map<BenefitGroup, string[]>();
    const hasDiscount =
        promo.totalDiscountCents > 0 ||
        promo.levers.some((l) => l === 'DEVICE_DISCOUNT' || l === 'ACCESSORY_DISCOUNT');
    const hasGift = promo.giftOptions.length > 0 || promo.levers.includes('GIFT');
    const wallet = walletBonusOf(promo.afterSale);
    const perks = describeAll(
        promo.afterSale.filter((b) => BENEFIT_LEVERS.has(b.lever) && b.lever !== 'WALLET_BONUS'),
        describe,
    );
    const benefits = [...(wallet ? [walletBonusText(wallet.amountCents)] : []), ...perks];
    const coupons = couponTextsOf(promo, describe);

    const onlyDiscount = !hasGift && benefits.length === 0 && coupons.length === 0;
    if (hasDiscount) out.set('discount', [discountText(promo, onlyDiscount)]);
    if (hasGift) out.set('gift', [giftPhrase(promo)]);
    if (benefits.length > 0) out.set('benefit', benefits);
    if (coupons.length > 0) out.set('coupon', coupons);
    return out;
}

/** Promos del equipo en orden alfabético, cada una en el grupo de su palanca principal. */
export function splitByBenefit(
    promotions: readonly EvaluatedSalePromotion[],
    describe: (benefit: AfterSaleBenefit) => string,
): BenefitPieces {
    const pieces: BenefitPieces = { gift: [], discount: [], benefit: [], coupon: [] };
    const sorted = [...promotions].sort((a, b) => a.name.localeCompare(b.name, 'es-MX'));

    for (const promo of sorted) {
        const texts = groupTexts(promo, describe);
        const group = GROUP_PRIORITY.find((g) => texts.has(g));
        if (group === undefined) {
            pieces.discount.push(textOnlyPiece(promo));
            continue;
        }
        const others = GROUP_PRIORITY.filter((g) => g !== group).flatMap((g) => texts.get(g) ?? []);
        pieces[group].push({
            promotionId: promo.promotionId,
            name: promo.name,
            group,
            texts: group === 'gift' ? [] : (texts.get(group) ?? []),
            gift:
                group === 'gift'
                    ? {
                          options: promo.giftOptions,
                          chooseCount: promo.giftChooseCount ?? 1,
                          includedInSale: promo.kind === 'SALE' && promo.giftOptions.length > 0,
                      }
                    : null,
            extras: others.length > 0 ? `+ ${joinSpanish(others.map(lowerFirst), 'y')}` : null,
            walletBonus: walletBonusOf(promo.afterSale) !== undefined,
            ...audienceOf(promo),
        });
    }
    return pieces;
}

/** Promo sin palanca reconocible: va con su texto al cliente. */
function textOnlyPiece(promo: EvaluatedSalePromotion): PromotionPiece {
    return {
        promotionId: promo.promotionId,
        name: promo.name,
        group: 'discount',
        texts: [promo.customerText],
        gift: null,
        extras: null,
        walletBonus: false,
        ...audienceOf(promo),
    };
}

function audienceOf(
    promo: EvaluatedSalePromotion,
): Pick<PromotionPiece, 'sureKeepOnly' | 'paymentLabel' | 'conditions'> {
    return {
        sureKeepOnly: isSureKeepOnly(promo),
        paymentLabel: paymentOptionsChipLabel(promo),
        conditions: conditionsText(promo.conditions, promo.blockedBy),
    };
}

function couponTextsOf(
    promo: EvaluatedSalePromotion,
    describe: (benefit: AfterSaleBenefit) => string,
): string[] {
    const texts = describeAll(
        promo.afterSale.filter((b) => b.lever === 'COUPON'),
        describe,
    );
    if (texts.length > 0 || !promo.levers.includes('COUPON')) return texts;
    return [describe({ lever: 'COUPON', amountCents: null, quantity: null })];
}

/** En una combo el texto al cliente mezcla todo; aquí va solo lo que baja el precio. */
function discountText(promo: EvaluatedSalePromotion, onlyDiscount: boolean): string {
    return !onlyDiscount && promo.totalDiscountCents > 0
        ? `Descuento de ${formatMXN(promo.totalDiscountCents / 100)}`
        : promo.customerText;
}

/** Alcance de un bono de venta según las promos de la tienda; `null` si todavía no llegan. */
export function saleBonusReach(
    promotionId: string,
    storePromotions: readonly StoreSalePromotionItem[] | undefined,
): string | null {
    if (storePromotions === undefined) return null;
    const promo = storePromotions.find((p) => p.promotionId === promotionId);
    if (!promo || !isCategoryWide(promo)) return 'este equipo';
    if (promo.scope.tiers !== null) return 'esta gama';
    const categories = promo.scope.productCategories;
    if (categories === null || categories.length === 0) return 'toda la tienda';
    const names = categories
        .map((c) => CATEGORY_PLURAL[c])
        .sort((a, b) => a.localeCompare(b, 'es-MX'));
    return `todos los ${joinSpanish(names, 'y')}`;
}

/** El backend paga un Wallet por promo: si llegan varios se toma el mayor, nunca la suma. */
function walletBonusOf(benefits: readonly AfterSaleBenefit[]): AfterSaleBenefit | undefined {
    let best: AfterSaleBenefit | undefined;
    for (const benefit of benefits) {
        if (benefit.lever !== 'WALLET_BONUS') continue;
        if (best === undefined || (benefit.amountCents ?? -1) > (best.amountCents ?? -1))
            best = benefit;
    }
    return best;
}
